/**
 * LT-Estimate-UX-01 Section 8 — Insights: O&P vs Win Rate.
 *
 * Time-boxed and DELIBERATELY narrow: one honest dashboard answering
 * "does our O&P markup correlate with winning the job?". If there
 * isn't enough data yet, the page says so — no fake trend lines, no
 * bar charts with two data points pretending to be a distribution.
 *
 * Every figure surfaces its `n=` sample size. Every empty state tells
 * the user WHAT is needed for the number to appear ("need ≥ 5 closed
 * tenders overall", "need ≥ 3 per band", etc).
 *
 * Design notes:
 *   • Bands table is always visible; individual bands with n<3 show n
 *     but no %. Under n<8 total, the whole table dims to a "need more
 *     data" call-out with the band structure preview beneath — the user
 *     can see what they'll get once data accumulates.
 *   • Job-type table only lists rows that clear MIN_SAMPLE_FOR_JOB_TYPE.
 *   • Job-type filter dropdown re-fetches with the filter applied.
 */
import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Info, Filter, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useJobTypeLibrary } from "../hooks/useJobTypeLibrary";
import { api } from "../lib/api";
import { ukDate } from "../lib/format";

const cls = (...xs) => xs.filter(Boolean).join(" ");
const pct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);
const num = (n) => (n == null ? "—" : Number(n).toLocaleString("en-GB"));

function Card({ title, subtitle, actions, children, testId, dim = false }) {
  return (
    <div
      data-testid={testId}
      className={cls(
        "rounded-lg border bg-surface flex flex-col",
        dim ? "border-line-soft opacity-80" : "border-line",
      )}
    >
      <header className="px-4 pt-3 pb-2 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-ink" style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {title}
          </h3>
          {subtitle && (
            <div className="text-ink-3 mt-0.5" style={{ fontSize: 11 }}>{subtitle}</div>
          )}
        </div>
        {actions}
      </header>
      <div className="px-4 pb-4 flex-1">{children}</div>
    </div>
  );
}

function EmptyMoreDataNeeded({ have, need, label }) {
  return (
    <div
      className="rounded-md border border-warning-soft bg-warning-soft/30 p-3 text-warning-strong flex items-start gap-2"
      style={{ fontSize: 12.5 }}
      data-testid="insights-empty-need-more-data"
    >
      <Info size={13} className="mt-0.5 flex-shrink-0" />
      <div>
        <div style={{ fontWeight: 600 }}>Not enough data yet.</div>
        <div className="mt-0.5 text-ink-3" style={{ fontSize: 12 }}>
          {label ? label : "Need"} at least {need} closed tenders — currently {have}.
          Numbers will appear here as tenders are marked won or lost.
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, sample, muted = false, testId }) {
  return (
    <div
      className={cls(
        "rounded-md border border-line-soft bg-surface-2/40 p-3",
      )}
      data-testid={testId}
    >
      <div className="text-ink-3" style={{ fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</div>
      <div
        className={cls("font-mono tnum mt-1", muted ? "text-ink-3" : "text-ink")}
        style={{ fontSize: 22, fontWeight: 700 }}
      >
        {value}
      </div>
      {sample != null && (
        <div className="text-ink-3 mt-0.5" style={{ fontSize: 10.5 }}>n = {sample}</div>
      )}
    </div>
  );
}

function SummaryCard({ data }) {
  const s = data.summary;
  const meaningful = s.is_meaningful;
  return (
    <Card
      title="Win rate at a glance"
      subtitle={
        meaningful
          ? `Across ${s.total_closed} closed tender${s.total_closed === 1 ? "" : "s"}`
          : `Need ≥ ${s.min_sample_for_win_rate} closed to compute a win rate`
      }
      testId="insights-card-summary"
    >
      {!meaningful ? (
        <EmptyMoreDataNeeded have={s.total_closed} need={s.min_sample_for_win_rate} label="Need" />
      ) : (
        <div className="grid grid-cols-4 gap-3">
          <StatTile label="Win rate" value={pct(s.win_rate_pct)} sample={s.total_closed} testId="insights-stat-win-rate" />
          <StatTile label="Won"   value={num(s.total_won)}  sample={null} testId="insights-stat-won" />
          <StatTile label="Lost"  value={num(s.total_lost)} sample={null} testId="insights-stat-lost" />
          <StatTile label="Avg O&P on wins vs losses" value={`${pct(s.avg_oap_pct_won)} / ${pct(s.avg_oap_pct_lost)}`} sample={s.total_closed} testId="insights-stat-avg-oap" />
        </div>
      )}
    </Card>
  );
}

function BandsCard({ data }) {
  const bb = data.by_band;
  const s = data.summary;
  const canShowTable = bb.is_meaningful;
  return (
    <Card
      title="Win rate by O&P band"
      subtitle={
        canShowTable
          ? `Bands with n < ${bb.min_sample_per_band} show a count but no percentage`
          : `Table shows once ${bb.min_sample_for_band_analysis}+ closed tenders exist (currently ${s.total_closed})`
      }
      dim={!canShowTable}
      testId="insights-card-by-band"
    >
      {!canShowTable && (
        <div className="mb-3">
          <EmptyMoreDataNeeded
            have={s.total_closed}
            need={bb.min_sample_for_band_analysis}
            label="Need"
          />
        </div>
      )}
      <table className="w-full">
        <thead>
          <tr>
            <th className="col-head text-left pb-1.5">Band</th>
            <th className="col-head text-right pb-1.5">Wins</th>
            <th className="col-head text-right pb-1.5">Losses</th>
            <th className="col-head text-right pb-1.5">n</th>
            <th className="col-head text-right pb-1.5">Win rate</th>
          </tr>
        </thead>
        <tbody>
          {bb.bands.map((b) => (
            <tr key={b.label} className="border-t border-line-soft" data-testid={`insights-band-${b.label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
              <td className="py-1.5 text-ink" style={{ fontSize: 13 }}>{b.label}</td>
              <td className="py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12.5 }}>{b.wins}</td>
              <td className="py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12.5 }}>{b.losses}</td>
              <td className="py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12.5 }}>{b.sample_size || "—"}</td>
              <td className="py-1.5 text-right font-mono tnum" style={{ fontSize: 12.5 }}>
                {b.win_rate_pct == null
                  ? <span className="text-ink-4" style={{ fontSize: 11 }}>{b.sample_size ? `n < ${bb.min_sample_per_band}` : "—"}</span>
                  : <span className="text-ink" style={{ fontWeight: 500 }}>{pct(b.win_rate_pct)}</span>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function JobTypeCard({ data, jobTypeLabelFor }) {
  const bj = data.by_job_type;
  const anyRows = (bj.rows || []).length > 0;
  return (
    <Card
      title="By job type"
      subtitle={
        anyRows
          ? `Job types with < ${bj.min_sample_for_job_type} closed tenders are hidden (${bj.hidden_count} hidden)`
          : `Nothing shown yet — need ≥ ${bj.min_sample_for_job_type} closed per job type`
      }
      testId="insights-card-by-job-type"
    >
      {!anyRows ? (
        <EmptyMoreDataNeeded have={0} need={bj.min_sample_for_job_type} label="Per job type, need" />
      ) : (
        <table className="w-full">
          <thead>
            <tr>
              <th className="col-head text-left pb-1.5">Job type</th>
              <th className="col-head text-right pb-1.5">Wins</th>
              <th className="col-head text-right pb-1.5">Losses</th>
              <th className="col-head text-right pb-1.5">n</th>
              <th className="col-head text-right pb-1.5">Win rate</th>
              <th className="col-head text-right pb-1.5">Avg O&P</th>
            </tr>
          </thead>
          <tbody>
            {bj.rows.map((r) => (
              <tr key={r.job_type} className="border-t border-line-soft">
                <td className="py-1.5 text-ink" style={{ fontSize: 13 }}>{jobTypeLabelFor(r.job_type)}</td>
                <td className="py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12.5 }}>{r.wins}</td>
                <td className="py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12.5 }}>{r.losses}</td>
                <td className="py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12.5 }}>{r.sample_size}</td>
                <td className="py-1.5 text-right font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 500 }}>{pct(r.win_rate_pct)}</td>
                <td className="py-1.5 text-right font-mono tnum text-ink-2" style={{ fontSize: 12.5 }}>{pct(r.avg_oap_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export default function Insights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jobTypes, setJobTypes] = useState([]);
  const [filter, setFilter] = useState("");
  const { labelFor: jobTypeLabelFor } = useJobTypeLibrary();

  const reload = () => {
    setLoading(true);
    const qs = filter ? `?job_type=${encodeURIComponent(filter)}` : "";
    api.get(`/insights/oap-vs-win-rate${qs}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load insights"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/insights/job-types").then((r) => setJobTypes(r.data.job_types || [])).catch(() => {});
  }, []);
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="mx-auto py-6" style={{ maxWidth: 1440, paddingLeft: 32, paddingRight: 32 }}>
      <header className="flex items-baseline justify-between mb-4">
        <div>
          <h1 className="text-ink" style={{ fontSize: 22, fontWeight: 700, fontFamily: "Space Grotesk, sans-serif" }}>
            Insights
          </h1>
          <div className="text-ink-3 mt-0.5" style={{ fontSize: 12.5 }}>
            O&amp;P markup vs win rate. Honest empty states — figures appear as data accumulates.
            {data && data.generated_at ? ` As of ${ukDate(data.generated_at)}.` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1.5 rounded-md border border-line px-2 py-1 bg-surface" data-testid="insights-filter-wrap">
            <Filter size={12} className="text-ink-3" />
            <select
              className="bg-transparent outline-none text-ink"
              style={{ fontSize: 12.5 }}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="insights-filter-select"
            >
              <option value="">All job types</option>
              {jobTypes.map((jt) => <option key={jt} value={jt}>{jobTypeLabelFor(jt)}</option>)}
            </select>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md border border-line px-2 py-1 text-ink-2 hover:bg-surface-2"
            onClick={reload}
            style={{ fontSize: 12.5 }}
            data-testid="insights-refresh-btn"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </header>

      {loading && (
        <div className="text-ink-4 py-8" style={{ fontSize: 12.5 }} data-testid="insights-loading">Loading…</div>
      )}

      {!loading && data && (
        <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }} data-testid="insights-grid">
          <div style={{ gridColumn: "span 2" }}>
            <SummaryCard data={data} />
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <BandsCard data={data} />
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <JobTypeCard data={data} jobTypeLabelFor={jobTypeLabelFor} />
          </div>
        </div>
      )}
    </div>
  );
}
